import { Cell } from '@table-library/react-table-library/table';
import numeral from 'numeral';

import { formatMetric } from './format';

/**
 * The DataTable column definitions every GSC tool table is built from.
 *
 * Three shapes cover the reports: two periods joined on one key (`comparisonColumns`), one period
 * of rows under a selected row (`contributorColumns`), and daily values aligned by day
 * (`multiTrendColumns`). They are defined once so that the same number never appears under two
 * different headings, and so a value Search Console did not return is named the same way
 * wherever it turns up.
 */

/**
 * Rows that exist in only one period have no movement to sort on. They sort to the bottom behind
 * a sentinel rather than being treated as a zero change, which would scatter them through the
 * middle of the table as if nothing had happened to them.
 */
const sortByDelta = (name) => (array) =>
    array.sort((a, b) => (a[name] ?? -Infinity) - (b[name] ?? -Infinity));

const sortByPeriodMetric = (period, metric) => (array) =>
    array.sort((a, b) => (a[period]?.[metric] ?? -Infinity) - (b[period]?.[metric] ?? -Infinity));

/** A metric for one period, or the reason there is no number to show. */
const periodCell = (t, period, metric) =>
    function PeriodMetricCell({ item, csvDownload }) {
        const value = item[period]?.[metric];

        if (value === null || value === undefined) {
            const label = t('tools.gsc.not_returned');
            return csvDownload ? label : <span className="text-muted">{label}</span>;
        }

        return formatMetric(metric, value);
    };

/**
 * A URL in a cell: the address as text, followed by the external-link mark the app's other page
 * tables carry. Only the mark opens the page, as it does in those tables. The address is held to
 * two lines on its own, beside the mark rather than around it, so a long address that is cut
 * still keeps the mark in view.
 */
function UrlLink({ href, label, className = '' }) {
    return (
        <span className="gsc-tool-url">
            <span className={`gsc-tool-url-text text-wrap ${className}`}>{label ?? href}</span>
            <a
                target="_blank"
                className="link-website d-inline-flex align-items-center justify-content-center"
                href={href}
                rel="noreferrer"
            >
                <i className="icon-external-link"></i>
            </a>
        </span>
    );
}

/** The key column: a URL is a link, query text is not. */
const keyColumn = ({ keyLabel, isUrl }) => ({
    label: keyLabel,
    key: 'key',
    data: 'key',
    sortType: 'string',
    cellSize: 'minmax(260px, 3fr)',
    Component: Cell,
    renderCell: ({ data, csvDownload }) => {
        if (csvDownload || !isUrl) return data;
        return <UrlLink href={data} />;
    },
});

/**
 * The two position columns. Discover and Google News report no ranking position, so they are
 * absent there rather than filled with a value Search Console never sent.
 */
const positionColumns = (t) => [
    {
        label: t('tools.gsc.columns.current_position'),
        key: 'current_position',
        data: 'current_position',
        cellSize: 'minmax(110px, 1fr)',
        sortType: sortByPeriodMetric('current', 'position'),
        Component: Cell,
        renderCell: periodCell(t, 'current', 'position'),
    },
    {
        label: t('tools.gsc.columns.prior_position'),
        key: 'prior_position',
        data: 'prior_position',
        cellSize: 'minmax(110px, 1fr)',
        sortType: sortByPeriodMetric('prior', 'position'),
        Component: Cell,
        renderCell: periodCell(t, 'prior', 'position'),
    },
];

/**
 * Two periods joined on one key: what each period returned, and the movement between them.
 *
 * The change columns are blank with a reason wherever the join could not be made — a key returned
 * in only one period has no change, and a baseline of zero clicks has an absolute change but no
 * percentage. Neither is shown as a 0.
 */
export function comparisonColumns(t, { hasPosition = true, keyLabel, isUrl = true } = {}) {
    return [
        keyColumn({ keyLabel: keyLabel ?? t('tools.gsc.columns.key'), isUrl }),
        {
            label: t('tools.gsc.columns.current_clicks'),
            key: 'current_clicks',
            data: 'current_clicks',
            cellSize: 'minmax(110px, 1fr)',
            sortType: sortByPeriodMetric('current', 'clicks'),
            Component: Cell,
            renderCell: periodCell(t, 'current', 'clicks'),
        },
        {
            label: t('tools.gsc.columns.prior_clicks'),
            key: 'prior_clicks',
            data: 'prior_clicks',
            cellSize: 'minmax(110px, 1fr)',
            sortType: sortByPeriodMetric('prior', 'clicks'),
            Component: Cell,
            renderCell: periodCell(t, 'prior', 'clicks'),
        },
        {
            label: t('tools.gsc.columns.delta_clicks'),
            key: 'deltaClicks',
            data: 'deltaClicks',
            cellSize: 'minmax(110px, 1fr)',
            sortType: sortByDelta('deltaClicks'),
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (item.deltaClicks === null) {
                    const label = t('tools.gsc.not_comparable');
                    return csvDownload ? label : <span className="text-muted">{label}</span>;
                }
                const formatted = numeral(item.deltaClicks).format('+0,0');
                if (csvDownload) return formatted;
                return (
                    <strong className={item.deltaClicks >= 0 ? 'gain' : 'loss'}>{formatted}</strong>
                );
            },
        },
        {
            label: t('tools.gsc.columns.delta_percent'),
            key: 'deltaPercent',
            data: 'deltaPercent',
            cellSize: 'minmax(100px, 1fr)',
            sortType: sortByDelta('deltaPercent'),
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (item.deltaPercent === null) {
                    const label = t('tools.gsc.no_baseline_row');
                    return csvDownload ? label : <span className="text-muted">{label}</span>;
                }
                const formatted = `${numeral(item.deltaPercent).format('+0.0')}%`;
                if (csvDownload) return formatted;
                return (
                    <span className={item.deltaPercent >= 0 ? 'gain' : 'loss'}>{formatted}</span>
                );
            },
        },
        {
            label: t('tools.gsc.columns.current_impressions'),
            key: 'current_impressions',
            data: 'current_impressions',
            cellSize: 'minmax(120px, 1fr)',
            sortType: sortByPeriodMetric('current', 'impressions'),
            Component: Cell,
            renderCell: periodCell(t, 'current', 'impressions'),
        },
        {
            label: t('tools.gsc.columns.prior_impressions'),
            key: 'prior_impressions',
            data: 'prior_impressions',
            cellSize: 'minmax(120px, 1fr)',
            sortType: sortByPeriodMetric('prior', 'impressions'),
            Component: Cell,
            renderCell: periodCell(t, 'prior', 'impressions'),
        },
        {
            label: t('tools.gsc.columns.current_ctr'),
            key: 'current_ctr',
            data: 'current_ctr',
            cellSize: 'minmax(100px, 1fr)',
            sortType: sortByPeriodMetric('current', 'ctr'),
            Component: Cell,
            renderCell: periodCell(t, 'current', 'ctr'),
        },
        {
            label: t('tools.gsc.columns.prior_ctr'),
            key: 'prior_ctr',
            data: 'prior_ctr',
            cellSize: 'minmax(100px, 1fr)',
            sortType: sortByPeriodMetric('prior', 'ctr'),
            Component: Cell,
            renderCell: periodCell(t, 'prior', 'ctr'),
        },
        ...(hasPosition ? positionColumns(t) : []),
    ];
}

/**
 * One period of rows for a selected row's contributors: the key and its four metrics, as returned.
 *
 * There is no change column because there is nothing to change against — a contributor list is
 * read inside one window — and no total row, because Search Console withholds rows below its own
 * thresholds and these are not guaranteed to sum to the row they belong to.
 */
export function contributorColumns(t, { hasPosition = true, keyLabel, isUrl = false } = {}) {
    const metricColumn = (metric, label, size) => ({
        label,
        key: metric,
        data: metric,
        cellSize: size,
        sortType: 'number',
        Component: Cell,
        renderCell: ({ data }) => formatMetric(metric, data),
    });

    return [
        keyColumn({ keyLabel: keyLabel ?? t('tools.gsc.columns.key'), isUrl }),
        metricColumn('clicks', t('tools.gsc.metrics.clicks_short'), 'minmax(100px, 1fr)'),
        metricColumn('impressions', t('tools.gsc.metrics.impressions_short'), 'minmax(120px, 1fr)'),
        metricColumn('ctr', t('tools.gsc.metrics.ctr_short'), 'minmax(100px, 1fr)'),
        ...(hasPosition
            ? [
                  metricColumn(
                      'position',
                      t('tools.gsc.metrics.position_short'),
                      'minmax(110px, 1fr)',
                  ),
              ]
            : []),
    ];
}

/**
 * One segment's daily value in one window, or the statement that Search Console did not return
 * that day for it.
 *
 * Which day came back is per segment, not per row: a brand query can have activity on a day its
 * non-brand counterpart has none, and the cell that has nothing says so rather than borrowing the
 * neighbouring column's answer.
 */
const trendValueCell = (t, metric, field) =>
    function TrendValueCell({ item }) {
        if (!item[`${field}Returned`]) return t('tools.gsc.not_returned');
        return formatMetric(metric, item[`${field}Value`]);
    };

const sortByReturned = (field) => (array) =>
    array.sort((a, b) => Number(a[`${field}Returned`]) - Number(b[`${field}Returned`]));

/**
 * The trend's daily rows, as a table.
 *
 * The windows are laid out side by side on the ordinal day that aligned them in the chart, with
 * each one's real date next to its values — the alignment the chart can only imply is spelled out
 * here. A day Search Console did not return is named as such rather than shown as a zero, in the
 * value column and again in the state column that can be sorted on.
 *
 * One column per segment per window. With a single unnamed segment that is the familiar current
 * and baseline pair; with several it is one column each, which is what makes a chart of two
 * segments readable as numbers — the lines can only show that they crossed, not by how much.
 *
 * @param {Array<{key: string, label: string}>} series the segments drawn, in chart order
 * @param {{hasBaseline?: boolean}} options whether the baseline columns are worth the width
 */
export function multiTrendColumns(t, metric, series, { hasBaseline = true } = {}) {
    const single = series.length === 1;

    const valueColumn = (item, baseline) => ({
        label: baseline
            ? single
                ? t('tools.gsc.trend.baseline_value')
                : t('tools.gsc.trend.baseline_of', { label: item.label })
            : single
            ? t('tools.gsc.trend.current_value')
            : item.label,
        key: baseline ? `${item.key}BaselineValue` : `${item.key}Value`,
        data: baseline ? `${item.key}BaselineValue` : `${item.key}Value`,
        sortType: 'number',
        cellSize: 'minmax(130px, 1fr)',
        Component: Cell,
        renderCell: trendValueCell(t, metric, baseline ? `${item.key}Baseline` : item.key),
    });

    return [
        {
            label: t('tools.gsc.trend.day_column'),
            key: 'day',
            data: 'day',
            sortType: 'number',
            cellSize: 'minmax(80px, 0.5fr)',
            Component: Cell,
        },
        {
            label: t('tools.gsc.trend.current_date'),
            key: 'currentDate',
            data: 'currentDate',
            sortType: 'date',
            cellSize: 'minmax(130px, 1fr)',
            Component: Cell,
            renderCell: ({ data }) => data ?? '—',
        },
        ...series.map((item) => valueColumn(item, false)),
        ...(hasBaseline
            ? [
                  {
                      label: t('tools.gsc.trend.baseline_date'),
                      key: 'baselineDate',
                      data: 'baselineDate',
                      sortType: 'date',
                      cellSize: 'minmax(130px, 1fr)',
                      Component: Cell,
                      renderCell: ({ data }) => data ?? '—',
                  },
                  ...series.map((item) => valueColumn(item, true)),
              ]
            : []),
        {
            // The state of the first segment stands for the row: the window is the same for all
            // of them, so this is whether the day itself came back at all.
            label: t('tools.gsc.exports.state'),
            key: 'state',
            data: `${series[0].key}Returned`,
            sortType: sortByReturned(series[0].key),
            cellSize: 'minmax(120px, 1fr)',
            Component: Cell,
            renderCell: ({ item }) =>
                item[`${series[0].key}Returned`]
                    ? t('tools.gsc.exports.returned')
                    : t('tools.gsc.not_returned'),
        },
    ];
}

/**
 * The two columns of a table of returned evidence: the field a row describes, and the value Google
 * returned for it. Used by the reports that read a record rather than a metric — URL Inspection and
 * Sitemap Status.
 *
 * Every value cell reads its row the same way: an absent value is "Not returned" (in words, in the
 * export too, so a downloaded file never turns it into an empty cell that reads as nothing), a URL
 * is a link, and a row's `tone` colours it with the shared gain/loss colours of the GSC tool
 * tables. The tone marks what Google returned — a failed fetch, a canonical that differs — and
 * never a cause the report cannot know. A whole column of URLs passes `isUrl`; a table mixing URLs
 * and text marks the URL rows with `item.isUrl` instead.
 */
export function evidenceFieldColumn(
    label,
    { key = 'field', cellSize = 'minmax(170px, 0.9fr)' } = {},
) {
    return {
        label,
        key,
        data: key,
        sortType: 'string',
        cellSize,
        Component: Cell,
    };
}

export function evidenceValueColumn(
    t,
    { label, key = 'value', cellSize = 'minmax(260px, 2.2fr)', isUrl = false },
) {
    return {
        label,
        key,
        data: key,
        sortType: 'string',
        cellSize,
        Component: Cell,
        renderCell: ({ item, data, csvDownload }) => {
            const missing = data === null || data === undefined || data === '';

            if (csvDownload) return missing ? t('tools.gsc.not_returned') : data;
            if (missing) return <span className="text-muted">{t('tools.gsc.not_returned')}</span>;

            if (isUrl || item.isUrl) {
                return <UrlLink href={data} label={item.linkLabel} className={item.tone} />;
            }

            return item.tone ? <span className={item.tone}>{data}</span> : data;
        },
    };
}
